/*
 * Documentation:
 * Login page — https://app.subframe.com/library?component=Login+page_dd9a2b65-1e87-4bbc-92cb-ca7d831cbfe7
 * Text Input — https://app.subframe.com/library?component=Text+Input_be48ca43-f8e7-4c0e-8870-d219ea11abfe
 * Button — https://app.subframe.com/library?component=Button_3b777358-b86b-40af-9327-891efc6826fe
 */

import React, { useState } from "react";
import { Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";

import { TextField } from "subframe";
import * as SubframeCore from "@subframe/core";
import { Button } from "subframe/components/Button";

import { get, set } from "utils/localStorage";
import { isEmpty as isStringEmpty } from "utils/string";

import { login } from "services/auth";
import { mixpanelLoginEvent } from "services/mixpanel";

import Logo from "../../../../assets/images/logoV2.svg";

function LoginPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [hasTriedLoggingIn, setHasTriedLoggingIn] = useState(false);
  const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleLoginAsync = async (email, password) => {
    try {
      setIsLoggingIn(true);
      const response = await login(email, password);
      const token = response?.data?.result?.token;
      const user = response?.data?.result?.user;
      const refreshToken = response?.data?.result?.["refresh-token"];

      if (!isStringEmpty(token)) {
        set("token", token);
        set("refreshToken", refreshToken);
        set("userRole", user);
        set("email", email);
        get("token");
        mixpanelLoginEvent(); // Emit login analytics event

        navigate("retention/V2");
      }
    } catch (error) {
      setErrorMessage(
        `Error while Logging In: ${error?.response?.data?.message}`
      );
      setIsErrorMessageShown(true);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLoginClick = () => {
    setHasTriedLoggingIn(true);

    if (isStringEmpty(email) || isStringEmpty(password)) {
      return;
    }

    handleLoginAsync(email, password);
  };

  return (
    <>
      <div className="w-full h-full items-start flex bg-default-background">
        <div className="grow shrink-0 basis-0 w-full h-full gap-12 pt-12 pr-12 pb-12 pl-12 items-center justify-center flex bg-brand-50">
          <div className="flex-col gap-6 items-start flex">
            <span className="text-default-font text-header font-header">
              Understand how your workers are with Levo.
            </span>
            <div className="flex-none h-px w-full flex-col gap-2 items-center flex bg-neutral-200" />
            <div className="gap-2 items-center flex">
              <SubframeCore.Icon
                className="text-brand-700 text-body font-body"
                name="FeatherCheckSquare"
              />
              <span className="text-default-font text-body font-body">
                Easy set up, no coding required.
              </span>
            </div>
            <div className="gap-2 items-center flex">
              <SubframeCore.Icon
                className="text-brand-700 text-body font-body"
                name="FeatherCheckSquare"
              />
              <span className="text-default-font text-body font-body">
                SMS communications, no downloads needed.
              </span>
            </div>
            <div className="gap-2 items-center flex">
              <SubframeCore.Icon
                className="text-brand-700 text-body font-body"
                name="FeatherCheckSquare"
              />
              <span className="text-default-font text-body font-body">
                Unlimited custom questions and timing.
              </span>
            </div>
          </div>
        </div>
        <div className="grow shrink-0 basis-0 w-full h-full flex-col gap-6 pt-12 pr-12 pb-12 pl-12 items-start flex">
          <img className="flex-none h-12 w-20" src={Logo} alt="levo-logo" />
          <div className="grow shrink-0 basis-0 h-full w-full flex-col gap-12 items-start justify-center flex">
            <div className="w-full flex-col gap-4 items-start justify-center flex">
              <TextField
                value={email}
                className="h-auto w-full flex-none"
                label="Email"
                onChange={handleEmailChange}
                error={hasTriedLoggingIn && isStringEmpty(email)}
                helpText={
                  hasTriedLoggingIn && isStringEmpty(email)
                    ? "Email is required."
                    : ""
                }
              >
                <TextField.Input placeholder="" />
              </TextField>

              <TextField
                value={password}
                className="h-auto w-full flex-none"
                label="Password"
                onChange={handlePasswordChange}
                helpText={
                  hasTriedLoggingIn && isStringEmpty(password)
                    ? "Password is required."
                    : ""
                }
                error={hasTriedLoggingIn && isStringEmpty(password)}
              >
                <TextField.Input type="password" placeholder="" />
              </TextField>
            </div>
            <Button
              disabled={isLoggingIn}
              variant="Brand Primary"
              size="Default"
              onClick={handleLoginClick}
              loading={isLoggingIn}
            >
              Login
            </Button>
          </div>
        </div>
      </div>
      <Snackbar
        open={isErrorMessageShown}
        autoHideDuration={6000}
        onClose={() => setIsErrorMessageShown(false)}
      >
        <MuiAlert
          onClose={() => setIsErrorMessageShown(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default LoginPage;
